import type { HttpErrorResponse } from "@angular/common/module.d-CnjH8Dlt";
import type { AbstractControl, FormGroup } from "@angular/forms";
import type { ApiErrorResponse } from "@core-interfaces/global/http";
import type { FormErrorMap, IBaseFormFactory } from "@core-ports/outputs/forms/base";

/**
 * BaseFormFactory es una clase abstracta que centraliza la lógica común para el manejo de formularios
 * en Angular, incluyendo:
 *
 * - Generación del formulario.
 * - Obtención de mensajes de error.
 * - Validación simplificada de controles.
 * - Aplicación de errores provenientes del backend.
 * - Limpieza de errores del backend.
 *
 * Esta clase está pensada para ser extendida por clases específicas que definan:
 * - La estructura del formulario (`generateForm`)
 * - El mapa de mensajes de error (`getErrorMap`)
 *
 * @typeParam T - Estructura del formulario tipada, donde cada clave corresponde a un AbstractControl.
 */
export abstract class BaseFormFactory<T extends { [K in keyof T]: AbstractControl<any, any> }>
    implements IBaseFormFactory<T> {

    /**
     * Debe ser implementado en las clases concretas para construir el FormGroup tipado.
     *
     * @returns FormGroup<T> - Formulario generado.
     */
    abstract generateForm(): FormGroup<T>;

    /**
     * Debe devolver un mapa de mensajes de error para cada control del formulario.
     * Este mapa se utiliza para mapear errores de validaciones locales (required, minlength, etc.)
     * a mensajes amigables.
     *
     * @param control - Control que se está validando.
     * @returns FormErrorMap<T> - Mapa de errores.
     */
    protected abstract getErrorMap(control: AbstractControl): FormErrorMap<T>;

    /**
     * Devuelve el mensaje de error asociado a un control del formulario.
     *
     * Orden de prioridad:
     * 1. Si existe un error del backend (`backend`), lo devuelve directamente.
     * 2. Si no, toma el primer error local y lo busca en el errorMap.
     *
     * @param controlName - Nombre del control dentro del formulario.
     * @param form - FormGroup contenedor.
     * @returns string | null - Mensaje de error o null si no hay error que mostrar.
     */
    getErrorMessage(controlName: keyof T, form: FormGroup<T>): string | null {
        const control = form.get(controlName as string);

        if (!control || !control.touched || !control.errors) {
            return null;
        }
        if (control.errors['backend']) {
            return control.errors['backend'];
        }
        const errorKeys = Object.keys(control.errors);
        if (!errorKeys.length) {
            return null;
        }

        const firstErrorKey = errorKeys[0];
        const errorMap = this.getErrorMap(control);
        const fieldErrors = errorMap[controlName] ?? errorMap._default;
        const errorMessage = fieldErrors[firstErrorKey];

        return errorMessage ?? null;
    }

    /**
     * Indica si un control es válido y ha sido "tocado" por el usuario.
     *
     * @param controlName - Nombre del control.
     * @param form - Formulario que contiene el control.
     * @returns boolean - true si es válido y touched, false en caso contrario.
     */
    isControlValid(controlName: keyof T, form: FormGroup<T>): boolean {
        const control = form.get(controlName as string);
        return !!(control && control.touched && control.valid);
    }

    /**
     * Indica si un control es inválido y ha sido "tocado".
     *
     * @param controlName - Nombre del control.
     * @param form - Formulario que contiene el control.
     * @returns boolean - true si es inválido y touched, false en caso contrario.
     */
    isControlInvalid(controlName: keyof T, form: FormGroup<T>): boolean {
        const control = form.get(controlName as string);
        return !!(control && control.touched && control.invalid);
    }

    /**
     * Aplica errores provenientes del backend al formulario.
     *
     * Los errores deben venir en la estructura:
     * {
     *   field_errors: {
     *     fieldName: ["mensaje1", "mensaje2", ...]
     *   }
     * }
     *
     * El primer mensaje del arreglo se asigna al control.
     *
     * @param error - HttpErrorResponse con field_errors.
     * @param form - FormGroup al que se le aplicarán los errores.
     * @returns boolean - true si se aplicaron errores, false en caso contrario.
     */
    applyBackendErrors(error: HttpErrorResponse | any, form: FormGroup<T>): boolean {
        const apiError: ApiErrorResponse | undefined = error?.error;
        if (!apiError?.field_errors) {
            return false;
        }

        let hasAppliedErrors = false;

        Object.entries(apiError.field_errors).forEach(([fieldName, errors]) => {
            const control = form.get(fieldName);
            if (control && Array.isArray(errors) && errors.length > 0) {
                control.setErrors({
                    backend: errors[0]
                });
                control.markAsTouched();

                hasAppliedErrors = true;
            }
        });

        return hasAppliedErrors;
    }

    /**
     * Elimina únicamente los errores provenientes del backend (clave `backend`)
     * sin afectar a los errores locales del control.
     *
     * @param form - FormGroup al que se le eliminarán los errores del backend.
     */
    clearBackendErrors(form: FormGroup<T>): void {
        Object.keys(form.controls).forEach(key => {
            const control = form.get(key);
            if (control?.errors?.['backend']) {
                const { backend, ...otherErrors } = control.errors;
                control.setErrors(Object.keys(otherErrors).length > 0 ? otherErrors : null);
            }
        });
    }
}
